import styled from '@emotion/styled'
import { ExtranetInviteTokenDto } from '@pharmac-extranet/extranet-api'
import {
  CheckIcon,
  ExtranetCatalogItemChevron,
  ExtranetContainer,
  ExtranetExpandArrowColumn,
  ExtranetTableColumn,
  ExtranetTableGroupColumn,
  ExtranetTableRow,
  formatDateTimeString,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import {
  PharmacButtonBase,
  PharmacIcon,
  PharmacSuccessToast,
  PharmacText,
  usePharmacToast,
} from '@pharmac/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useAuthenticationContext } from '../../features/authentication/useAuthentication'
import { api } from '../../services/HttpService'
import { __CUSTOMER_INVITE_LINKS_QUERY_KEY } from '../../views/customer/CustomerInviteLinksView'
import { ConfirmationModal } from '../confirmation-modal/ConfirmationModal'
import { InviteLinkMobileDetails } from './InviteLinkMobileDetails'

const IconColumn = styled(ExtranetTableColumn)`
  display: flex;
  justify-content: center;
  width: 100%;
`

type Props = {
  token: ExtranetInviteTokenDto
}

export const addClock = (date: string, clockText: string) => {
  const yyMMdd = formatDateTimeString(date).slice(0, 10)
  const HHmm = formatDateTimeString(date).slice(11, 16)

  return yyMMdd + ` ${clockText} ` + HHmm
}

export const InviteLinkItem = ({ token }: Props) => {
  const { minDesktop } = useBreakpoint()
  const [deleteModal, setDeleteModal] = useState<boolean>(false)
  const { t } = useLanguage(['extranet'])
  const toastHook = usePharmacToast()
  const queryClient = useQueryClient()
  const { isPharmacCustomerService } = useAuthenticationContext()

  const { mutate: deleteCustomerUser } = useMutation({
    mutationFn: (token: { customerId: number; tokenId: number }) =>
      api.customers.deleteInviteToken(token.customerId, token.tokenId),
    onSuccess: async () => {
      toastHook.add({
        autoClose: true,
        autoCloseTimeout: 4000,
        toast: (
          <PharmacSuccessToast>{t('inviteLinks.cancelInviteLinkSuccess')}</PharmacSuccessToast>
        ),
      })
      await queryClient.invalidateQueries({
        queryKey: [__CUSTOMER_INVITE_LINKS_QUERY_KEY],
      })
    },
  })

  const handleDeleteInviteToken = () => {
    deleteCustomerUser({ customerId: Number(token.customerId), tokenId: Number(token.id) })
  }

  return (
    <>
      <ExtranetTableRow
        style={{ background: 'var(--color-accent-1)' }}
        expand={
          <>
            {minDesktop ? (
              <ExtranetContainer
                full
                paddingMedium
              >
                <ExtranetContainer
                  flexRow
                  paddingLargeRight
                >
                  <PharmacText
                    weight="bold"
                    paddingSmallRight
                  >
                    {t('user.userRoles')}:
                  </PharmacText>
                  {token.roles.map((role, i) => (
                    <PharmacText
                      paddingSmallRight
                      key={role.id}
                    >
                      {t(`customer.roles.${role.role}`)}
                      {i < token.roles.length - 1 && <>,</>}
                    </PharmacText>
                  ))}
                </ExtranetContainer>
              </ExtranetContainer>
            ) : (
              <InviteLinkMobileDetails
                token={token}
                deleteInviteToken={handleDeleteInviteToken}
              />
            )}
          </>
        }
        toggleExpand={() => null}
        children={({ isExpanded }) => (
          <>
            <ExtranetTableGroupColumn>
              <ExtranetTableRow>
                {minDesktop && (
                  <ExtranetTableColumn
                    size={1}
                    marginSmallLeft
                  >
                    {token.user.firstName} {token.user.lastName}
                  </ExtranetTableColumn>
                )}
                <ExtranetTableColumn
                  size={1.5}
                  marginSmallLeft={!minDesktop}
                >
                  {token.userEmail}
                </ExtranetTableColumn>
                {minDesktop && (
                  <ExtranetTableColumn size={1}>
                    {addClock(token.validTo, t('clock', { ns: 'common' }))}
                  </ExtranetTableColumn>
                )}
                {minDesktop && (
                  <ExtranetTableColumn
                    style={{ justifyContent: 'center' }}
                    size={1}
                  >
                    <CheckIcon icon="checkCircle" />
                  </ExtranetTableColumn>
                )}
                {minDesktop && (
                  <ExtranetTableColumn size={1}>
                    {token.roles?.length} {t('pcs', { ns: 'common' })}
                  </ExtranetTableColumn>
                )}
                {minDesktop && (
                  <IconColumn
                    size={1}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <PharmacButtonBase
                      disabled={isPharmacCustomerService}
                      onClick={() => setDeleteModal(true)}
                    >
                      <PharmacIcon icon="trash2" />
                    </PharmacButtonBase>
                  </IconColumn>
                )}
                <ExtranetExpandArrowColumn>
                  <ExtranetCatalogItemChevron isExpanded={isExpanded} />
                </ExtranetExpandArrowColumn>
              </ExtranetTableRow>
            </ExtranetTableGroupColumn>
          </>
        )}
      />
      <ConfirmationModal
        title={t('inviteLinks.cancelInviteLinkModal')}
        content={
          <ExtranetContainer
            full
            flexRow
          >
            <PharmacText>{t('inviteLinks.cancelInviteLinkDescription')}</PharmacText>
            <PharmacText
              weight="semibold"
              style={{ paddingLeft: 'var(--spacing-tiny)' }}
            >
              {token.userEmail}
            </PharmacText>
          </ExtranetContainer>
        }
        onSubmit={() => handleDeleteInviteToken()}
        onClose={() => setDeleteModal(false)}
        showOnProp={deleteModal}
      />
    </>
  )
}
